/**
 * MailVorlagenPage — Top-Level-Reiter "Mail-Vorlagen" (Phase 8 Plan 03 Umbau).
 *
 * Layout:
 *   - Top-Tabs: Mail-Vorlagen | Meine Signaturen
 *   - In Mail-Vorlagen-Tab: 3 Stage-Sub-Tabs (Erstkontakt / Follow-up 1 / Follow-up 2)
 *   - Pro Stage: <TemplateProfilesEditor templateKey={stage} /> mit Liste links + Editor rechts
 *
 * Vor Phase 8 (5.2-03): 4 Top-Tabs (Erstkontakt | FU1 | FU2 | Meine Signaturen),
 *   pro Stage genau 1 Vorlage editierbar via TemplateEditorV2. Cutover komplett —
 *   TemplateEditorV2 wird entfernt, die Tab-Struktur umgebaut.
 *
 * Lazy-loaded vom App.tsx via React.lazy() — TipTap-Bundle ist nicht im
 * initial Programm-Chunk.
 */
import { useState } from 'react';
import SignatureProfilesEditor from '../components/templates/SignatureProfilesEditor';
import TemplateProfilesEditor from '../components/templates/TemplateProfilesEditor';
import type { LeadTemplateKey } from '../types';

type TopTab = 'templates' | 'signatures';

const STAGES: Array<{ key: LeadTemplateKey; label: string }> = [
  { key: 'erstkontakt', label: 'Erstkontakt' },
  { key: 'followup_1', label: 'Follow-up 1' },
  { key: 'followup_2', label: 'Follow-up 2' },
];

export default function MailVorlagenPage() {
  const [topTab, setTopTab] = useState<TopTab>('templates');
  const [stage, setStage] = useState<LeadTemplateKey>('erstkontakt');

  const topTabClass = (key: TopTab) =>
    topTab === key
      ? 'px-4 py-2 text-sm font-semibold rounded-t bg-[#E9FF57] text-[#1A1A1A] border border-[#DAD9D9] border-b-0'
      : 'px-4 py-2 text-sm font-medium rounded-t bg-[#DAD9D9] text-[#1A1A1A] border border-[#DAD9D9] border-b-0 hover:bg-gray-200 transition-colors';

  const stageTabClass = (key: LeadTemplateKey) =>
    stage === key
      ? 'px-3 py-1.5 text-sm font-semibold rounded bg-[#E9FF57] text-[#1A1A1A] border border-[#DAD9D9]'
      : 'px-3 py-1.5 text-sm font-medium rounded bg-white text-[#1A1A1A] border border-[#DAD9D9] hover:bg-gray-100 transition-colors';

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-[#1A1A1A]">Mail-Vorlagen</h2>
        <p className="mt-1 text-sm text-gray-600">
          Mehrere benannte Vorlagen pro Stufe (Erstkontakt / Follow-up 1 / Follow-up 2).
          Die Default-Vorlage einer Stufe wird vom Auto-Workflow versendet.
          Aenderungen wirken sofort fuer neue Mails (nicht retroaktiv).
        </p>
      </div>

      <div className="flex gap-1 border-b border-[#DAD9D9]">
        <button type="button" onClick={() => setTopTab('templates')} className={topTabClass('templates')}>
          Mail-Vorlagen
        </button>
        <button type="button" onClick={() => setTopTab('signatures')} className={topTabClass('signatures')}>
          Meine Signaturen
        </button>
      </div>

      <div className="mt-4">
        {topTab === 'signatures' && <SignatureProfilesEditor />}
        {topTab === 'templates' && (
          <div className="space-y-4">
            <div className="flex gap-2">
              {STAGES.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => setStage(s.key)}
                  className={stageTabClass(s.key)}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <TemplateProfilesEditor key={stage} templateKey={stage} />
          </div>
        )}
      </div>
    </div>
  );
}
